import { useEffect, useRef } from 'react';
import { Button } from '../../../ui';

/**
 * The question asked before something that cannot be undone — a field taken off every item, a
 * row that disappears from the live page. A real <dialog>, as People uses: `window.confirm`
 * blocks the page, cannot be styled, and cannot be driven by a test.
 *
 * `question` null is closed. Escape is Cancel, the way the phone's cancel button is the default.
 */
export default function ConfirmDialog({
  question,
  onConfirm,
  onCancel,
}: {
  question: { title: string; body: string; confirm: string; cancel: string } | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = dialog.current;
    if (!el) return;
    if (question && !el.open) el.showModal();
    if (!question && el.open) el.close();
  }, [question]);

  return (
    <dialog
      ref={dialog}
      aria-label={question?.title ?? 'Confirm'}
      className="confirm-dialog"
      onCancel={(e) => {
        e.preventDefault();
        onCancel();
      }}
    >
      {question && (
        <>
          <h2 className="type-title">{question.title}</h2>
          <p className="type-body">{question.body}</p>
          <div className="build-actions">
            <Button title={question.confirm} variant="dangerSoft" onPress={onConfirm} />
            <Button title={question.cancel} variant="ghost" onPress={onCancel} />
          </div>
        </>
      )}
    </dialog>
  );
}
